import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {type ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import QualityDataLegend from 'model-hub/scientific-models/structure-prediction/QualityDataLegend';
import {StructurePredictionExternalFile} from 'model-hub/scientific-models/structure-prediction/structurePredictionConstants';
import StructurePredictionInfo from 'model-hub/scientific-models/structure-prediction/StructurePredictionInfo';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ScientificModelPreferences} from '../ScientificModelConstants';

export interface StructureViewerHeaderProps {
  structureFile: StructurePredictionExternalFile;
  model: ScientificModelNode;
  modelVersion: string;
  downloadStructureFile: () => void;
}

export default function StructureViewerHeader({
  structureFile,
  model,
  modelVersion,
  downloadStructureFile,
}: StructureViewerHeaderProps): JSX.Element {
  const [hasDismissedPopover, setHasDismissedPopover] = usePreference<boolean>(
    ScientificModelPreferences.HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER
  );
  const [popoverProps, setPopoverProps] = useState<
    {isOpen: boolean; interaction: FloatingInteraction.CONTROLLED} | {interaction: FloatingInteraction.HOVER}
  >(() => {
    // If user has already dismissed the popover, start with hover interaction
    if (hasDismissedPopover) {
      return {interaction: FloatingInteraction.HOVER};
    }
    return {
      isOpen: true,
      interaction: FloatingInteraction.CONTROLLED,
    };
  });

  useEffect(() => {
    // Only set the timeout if the user hasn't dismissed the popover before
    if (!hasDismissedPopover) {
      const timeoutId = setTimeout(() => {
        setPopoverProps({interaction: FloatingInteraction.HOVER});
      }, 5000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return () => {};
  }, [hasDismissedPopover]);

  const handleDismissPopover = (): void => {
    setHasDismissedPopover(true);
    setPopoverProps({interaction: FloatingInteraction.HOVER});
  };

  return (
    <div
      className={css(styles.optionButtonsLayout)}
      {...getTestProps({component: 'StructureViewerHeader', element: 'root'})}
    >
      <div className={css(styles.leftSection)}>
        <div>
          <Popover
            {...popoverProps}
            placement={FloatingPlacement.RIGHT_START}
            renderContent={() => (
              <QualityDataLegend
                onDismiss={
                  popoverProps.interaction === FloatingInteraction.CONTROLLED
                    ? handleDismissPopover
                    : undefined
                }
              />
            )}
          >
            <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
          </Popover>
          <span className={css(styles.infoText)}>Model confidence</span>
        </div>
        <StructurePredictionInfo
          modelDisplayName={model.displayName}
          modelVersion={modelVersion}
          className={css(styles.predictionInfo)}
        />
      </div>
      <Button
        className={css(styles.withoutButtonFocusStyling)}
        text={`Download ${
          structureFile.previewType === EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER ? 'mmCIF (PDBx)' : 'PDB'
        }`}
        buttonStyle={ButtonStyle.PRIMARY}
        size={ButtonSize.MINI}
        onClick={() => {
          downloadStructureFile();
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Should match the value inherited from .buffer-options
    height: '35px',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  predictionInfo: {
    margin: '0',
  },
  withoutButtonFocusStyling: {':focus': {textDecoration: 'none'}},
});
